import * as React from 'react';

import { Input, type InputProps } from './input';

export interface NumberInputProps
  extends Omit<InputProps, 'value' | 'onChange' | 'type'> {
  /** Numeric value, or null/undefined for an EMPTY box (the placeholder shows). */
  value: number | null | undefined;
  /** Emits the parsed number, or `null` when the box is cleared. */
  onChange: (value: number | null) => void;
}

const toText = (v: number | null | undefined) =>
  v === null || v === undefined || Number.isNaN(v) ? '' : String(v);

/**
 * Controlled numeric input that can be genuinely EMPTY.
 *
 * Why this exists — the bug it prevents:
 * Binding a raw `<Input type="number" value={n}>` and coercing empty→0 makes the
 * field un-clearable and stacks a forced leading 0 (typing over "0" yields
 * "012"). This component keeps an internal string so the box can be empty
 * (showing its `placeholder` as a watermark), parses to a number on change,
 * normalizes away leading zeros, and emits `null` when cleared.
 *
 * ⛔ APP RULE: never bind a raw `<Input type="number">` to a numeric form field —
 * always use <NumberInput>.
 * For a "0-as-watermark" field whose schema stores a number (not null), map at
 * the call site:
 *   value={field.value || null}
 *   placeholder="0"
 *   onChange={(n) => field.onChange(n ?? 0)}
 */
export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  function NumberInput({ value, onChange, ...props }, ref) {
    const [text, setText] = React.useState<string>(() => toText(value));

    // Sync down when the external value changes (form.reset, async load) — but
    // not while it merely echoes what the user just typed.
    React.useEffect(() => {
      const current = text === '' ? null : Number(text);
      if (current !== value) setText(toText(value));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
      <Input
        {...props}
        ref={ref}
        type="number"
        inputMode="numeric"
        value={text}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') {
            setText('');
            onChange(null);
            return;
          }
          const n = e.target.valueAsNumber;
          if (Number.isNaN(n)) {
            // Mid-typing states like "-" or "1." — keep raw, don't emit junk.
            setText(raw);
            return;
          }
          // Normalize ("01" → "1") unless the user is mid-typing a decimal/sign.
          setText(raw.endsWith('.') || raw.endsWith('-') ? raw : String(n));
          onChange(n);
        }}
      />
    );
  },
);
